import React from 'react';
import { Checkbox, Group, Stack, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import type { HandleDefinition, DeclaredInput } from '../../types';
import classes from './NodeConfigDrawer.module.css';

export type DeclaredInputsSectionProps = {
  nodeId: string;
  inputs: HandleDefinition[];
  declaredInputs: DeclaredInput[];
  onDeclaredInputsChange: ( declaredInputs: DeclaredInput[]) => void;
};

/**
 * Section within the NodeConfigDrawer that lets users expose
 * individual node inputs as top-level rule inputs.
 */
export function DeclaredInputsSection({
  nodeId,
  inputs,
  declaredInputs,
  onDeclaredInputsChange
}: DeclaredInputsSectionProps ) {
  const isDeclared = React.useCallback(( inputName: string ) => {
    return declaredInputs.some(
      ( di ) => di.nodeId === nodeId && di.inputName === inputName
    );
  }, [ declaredInputs, nodeId ]);

  const handleToggle = React.useCallback(( inputName: string, checked: boolean ) => {
    if ( checked ) {
      onDeclaredInputsChange([
        ...declaredInputs,
        { nodeId, inputName }
      ]);
    } else {
      onDeclaredInputsChange(
        declaredInputs.filter(
          ( di ) => !( di.nodeId === nodeId && di.inputName === inputName )
        )
      );
    }
  }, [ declaredInputs, nodeId, onDeclaredInputsChange ]);

  if ( inputs.length === 0 ) {
    return null;
  }

  return (
    <div>
      <Group gap={6} className={classes.sectionTitle} style={{ marginTop: 16 }}>
        <span>Declared Inputs</span>
        <Tooltip
          label="Expose selected inputs as top-level rule inputs so they can be provided when the rule is executed."
          multiline
          w={220}
          withArrow
        >
          <IconInfoCircle size={14} style={{ cursor: 'help', opacity: 0.7 }} />
        </Tooltip>
      </Group>

      <Stack gap="xs">
        {inputs.map(( input ) => {
          const declared = isDeclared( input.name );
          return (
            <div
              key={input.name}
              className={declared ? classes.declaredInputRow : classes.inputRow}
            >
              <Checkbox
                label={input.name}
                checked={declared}
                onChange={( e ) => handleToggle( input.name, e.currentTarget.checked )}
                styles={{
                  label: {
                    color: 'var(--drawer-text)',
                    fontSize: 13,
                    fontWeight: declared ? 600 : 400
                  }
                }}
              />
              <span className={classes.inputTypeBadge}>{input.type}</span>
            </div>
          );
        })}
      </Stack>
    </div>
  );
}
